// 담당: 현정
// Hub Program(연재 프로그램) 회차별 성과 추적 — Performance 탭 하단 블록.
//
// 기존 Hub Programs 대시보드(server/hub-assets/programs.jsx)의 구조를 그대로 옮긴다:
//   기간 토글 → 비교 테이블(+ 채널 평균 footer) → 프로그램 카드 → (카드 클릭) 회차 상세.
// 소스는 cic 가 아니라 **포털 Mongo**다 (creatorsApi 의 fetchHubPrograms 주석 참고).
//
// 표기 규칙은 워크스페이스 공통을 따른다 — 값 없음은 "—"(0 표기 금지), 시작 전 프로그램은
// 지표 0 이 아니라 "시작 전"으로 적는다.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  fetchHubProgramDetails,
  fetchHubPrograms,
  type HubChannelBaseline,
  type HubEpisodeAnalytics,
  type HubProgramDetails,
  type HubProgramRow,
  type HubProgramsOverview,
  type HubQuadrant,
  type HubSignalLevel,
} from '../../creatorsApi'
import { InfoTip } from '../InfoTip'
import { formatCount } from '../overview/format'

/** 값 없음은 "—". 0은 실제 0으로 적는다(둘을 뭉개면 "데이터 없음"과 "성과 0"이 같은 얼굴이 된다). */
const pct = (v: number | null | undefined, digits = 1): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(digits)}%`
const count = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : formatCount(v)
const signed = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v >= 0 ? '+' : ''}${Math.round(v).toLocaleString('ko-KR')}`

const SIGNAL_CLASS: Record<HubSignalLevel, string> = {
  green: 'bg-status-success',
  yellow: 'bg-status-warning',
  red: 'bg-status-danger',
}
const QUADRANT_LABEL: Record<HubQuadrant, string> = {
  all_round: '올라운드',
  reactive: '반응형',
  immersive: '몰입형',
  both_weak: '둘 다 약함',
  insufficient: '표본 부족',
}

export function HubProgramTracking({ creatorId }: { creatorId: string }) {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [channelLinked, setChannelLinked] = useState(true)
  const [overview, setOverview] = useState<HubProgramsOverview | null>(null)
  const [period, setPeriod] = useState<string>('')
  const [openId, setOpenId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setState('loading')
    try {
      const res = await fetchHubPrograms(creatorId)
      setChannelLinked(res.channelLinked)
      setOverview(res.overview)
      setPeriod((p) => (p && res.overview?.periods.includes(p) ? p : (res.overview?.periods[0] ?? '')))
      setState('ok')
    } catch {
      setState('error')
    }
  }, [creatorId])

  useEffect(() => {
    setOpenId(null)
    void load()
  }, [load])

  const programs = overview?.list ?? []

  return (
    <div className="mt-5 border border-line-hairline bg-surface-base p-[18px_20px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 font-text text-[14px] font-medium text-fg-1">
          Hub Program 퍼포먼스 · 프로그램별 트래킹
          <InfoTip
            label="Hub Program"
            text="크리에이터의 연재 프로그램(시리즈)별 회차 성과입니다. 프로그램에 등록한 별칭이 영상 제목과 맞으면 자동으로 회차가 편입됩니다."
          />
        </div>
        {state === 'ok' && channelLinked && (
          <div className="flex items-center gap-2">
            {(overview?.periods.length ?? 0) > 1 && (
              <div className="flex items-center border border-line-soft">
                {overview!.periods.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={[
                      'px-2.5 py-1 font-text text-[11px]',
                      p === period ? 'bg-ink-50 font-medium text-ink-800' : 'text-fg-3',
                    ].join(' ')}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {state === 'loading' && <Skeleton />}
      {state === 'error' && (
        <Empty title="프로그램 정보를 불러오지 못했습니다" sub="잠시 후 다시 시도해주세요." />
      )}

      {/* 빈 상태 2종을 구분한다 — 채널 미연동은 등록 자체가 불가능하므로 안내가 달라야 한다. */}
      {state === 'ok' && !channelLinked && (
        <Empty
          title="YouTube 채널이 연동되지 않았습니다"
          sub="채널이 연동되고 성과 수집이 시작되면 연재 프로그램별 회차 성과를 추적할 수 있습니다."
        />
      )}
      {state === 'ok' && channelLinked && programs.length === 0 && (
        <Empty
          title="등록된 Hub Program이 없습니다"
          sub="Hub Programs 대시보드에서 프로그램을 등록하면 회차별 성과가 이곳에 표시됩니다."
        />
      )}

      {state === 'ok' && channelLinked && programs.length > 0 && overview && (
        <>
          <ComparisonTable
            programs={programs}
            period={period}
            baseline={overview.channelBaseline}
            onOpen={(id) => setOpenId((cur) => (cur === id ? null : id))}
            openId={openId}
          />
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p) => (
              <ProgramCard
                key={p.id}
                row={p}
                period={period}
                open={openId === p.id}
                onOpen={() => setOpenId((cur) => (cur === p.id ? null : p.id))}
              />
            ))}
          </div>
          {openId && (
            <ProgramDetail
              creatorId={creatorId}
              programId={openId}
              baseline={overview.channelBaseline}
              onClose={() => setOpenId(null)}
            />
          )}
        </>
      )}

    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   비교 테이블 — 기존 대시보드의 "전체 Hub Program 비교"와 같은 열 구성.
   footer 의 채널 평균은 **1편당** 값이라 프로그램 열(기간 합계)과 기준이 다르다 → 라벨로 명시.
   ───────────────────────────────────────────────────────────── */
function ComparisonTable({
  programs,
  period,
  baseline,
  onOpen,
  openId,
}: {
  programs: HubProgramRow[]
  period: string
  baseline: HubChannelBaseline
  onOpen: (id: string) => void
  openId: string | null
}) {
  const rows = programs.map((p) => ({ p, m: p.metrics[period] ?? p.metrics['총기간'] }))
  const maxViews = Math.max(1, ...rows.map((r) => (r.p.notStarted ? 0 : (r.m?.viewsTotal ?? 0))))
  const baselineKeys = Object.keys(baseline?.windows ?? {})

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse font-text text-[13px]">
        <thead>
          <tr className="border-b border-line-soft text-left text-[11px] tracking-[0.08em] text-fg-3">
            <th className="py-2 pr-3 font-normal">프로그램</th>
            <th className="py-2 pr-3 text-right font-normal">시작</th>
            <th className="py-2 pr-3 text-right font-normal">회차</th>
            <th className="py-2 pr-3 text-right font-normal">총 조회수</th>
            <th className="py-2 pr-3 text-right font-normal">구독전환</th>
            <th className="py-2 pr-3 text-right font-normal">시청률</th>
            <th className="py-2 pr-3 text-right font-normal">시청시간비율</th>
            <th className="py-2 text-right font-normal">참여도</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, m }) => (
            <tr
              key={p.id}
              onClick={() => onOpen(p.id)}
              className={[
                'cursor-pointer border-b border-line-hairline',
                openId === p.id ? 'bg-ink-50' : '',
              ].join(' ')}
            >
              <td className="py-2.5 pr-3">
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-[7px] w-[7px] flex-none"
                    style={{ background: p.accent }}
                    aria-hidden
                  />
                  <span className="min-w-0 truncate font-medium text-ink-800">{p.name}</span>
                </span>
              </td>
              {p.notStarted ? (
                <td className="py-2.5 text-right text-fg-3" colSpan={7}>
                  시작 전 · 데이터 없음
                </td>
              ) : (
                <>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">
                    {p.daysSinceStart == null ? '—' : `D+${p.daysSinceStart}`}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">{p.episodes}화</td>
                  <td className="py-2.5 pr-3 text-right">
                    <span className="tabular-nums font-medium text-ink-800">
                      {count(m?.viewsTotal)}
                    </span>
                    <span className="mt-1 block h-[3px] w-full bg-surface-soft">
                      <span
                        className="block h-full"
                        style={{
                          width: `${Math.max(3, ((m?.viewsTotal ?? 0) / maxViews) * 100)}%`,
                          background: p.accent,
                        }}
                      />
                    </span>
                  </td>
                  {/* Analytics 미수집 채널은 시청률·구독전환이 구조적으로 비어 있다 → 0 아닌 "—" */}
                  <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">
                    {p.analyticsReady ? signed(m?.subsTotal) : '—'}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">
                    {p.analyticsReady ? pct(m?.viewRate) : '—'}
                  </td>
                  <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">
                    {p.analyticsReady ? pct(m?.watch) : '—'}
                  </td>
                  <td className="py-2.5 text-right tabular-nums text-fg-2">
                    {pct(m?.eng, 2)}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
        {baselineKeys.length > 0 && (
          <tfoot>
            {baselineKeys.map((k, i) => {
              const w = baseline.windows[k]
              const has = w && w.n > 0
              return (
                <tr
                  key={k}
                  className={i === 0 ? 'border-t-2 border-line-soft' : 'border-t border-line-hairline'}
                >
                  <td className="py-2 pr-3 text-fg-3">
                    <span className="mr-1.5 border border-line-soft px-1 py-px text-[10px]">
                      채널 평균
                    </span>
                    최근 {Math.round((w?.windowDays ?? 0) / 30)}개월
                  </td>
                  <td className="py-2 pr-3 text-right text-[11px] text-fg-3">1편당</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-fg-3">
                    {has ? `${w.n}편` : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-fg-3">
                    {has ? count(w.views) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-fg-3">
                    {has ? signed(w.subs) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-fg-3">
                    {has ? pct(w.viewRate) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-fg-3">
                    {has ? pct(w.watch) : '—'}
                  </td>
                  <td className="py-2 text-right tabular-nums text-fg-3">
                    {has ? pct(w.eng, 2) : '—'}
                  </td>
                </tr>
              )
            })}
          </tfoot>
        )}
      </table>
      <p className="mt-2 font-text text-[11px] text-fg-3">
        채널 평균은 롱폼+숏폼 전체의 <b>1편당</b> 값이고, 프로그램 행의 조회수·구독전환은 해당 기간
        <b> 합계</b>입니다. 시청률·시청시간비율·참여도는 양쪽 모두 회차 평균이라 직접 비교됩니다.
      </p>
    </div>
  )
}

/* ── 프로그램 카드 ── */
function ProgramCard({
  row,
  period,
  open,
  onOpen,
}: {
  row: HubProgramRow
  period: string
  open: boolean
  onOpen: () => void
}) {
  const m = row.metrics[period] ?? row.metrics['총기간']
  const spark = row.sparkData?.map((d) => d.y) ?? []
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen()
      }}
      className={[
        'group cursor-pointer border p-[14px_16px]',
        open ? 'border-brand-blue' : 'border-line-hairline',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className="inline-block h-[6px] w-[6px] flex-none"
            style={{ background: row.accent }}
            aria-hidden
          />
          <span className="min-w-0 truncate font-display text-[14px] font-medium text-ink-800">
            {row.name}
          </span>
        </span>
        <span className="flex flex-none items-center gap-1">
          <ChevronDown
            className={['h-4 w-4 text-fg-3', open ? 'rotate-180' : ''].join(' ')}
            aria-hidden
          />
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-[26px] font-bold tabular-nums text-ink-800">
          {row.notStarted ? '—' : count(m?.views)}
        </span>
        <span className="font-text text-[11px] text-fg-3">회차 평균 조회수</span>
      </div>

      {spark.length > 1 && <Sparkline data={spark} color={row.accent} />}

      <div className="mt-3 flex items-center justify-between border-t border-line-hairline pt-2.5 font-text text-[11px] text-fg-3">
        <span>{row.notStarted ? '시작 전' : `${row.episodes}화 · D+${row.daysSinceStart ?? '—'}`}</span>
        <span>
          {row.lastUploadDaysAgo == null ? '업로드 없음' : `최근 업로드 ${row.lastUploadDaysAgo}일 전`}
        </span>
      </div>
      <div className="mt-1 font-text text-[11px] text-fg-3">
        발행 목표 · {row.targetCadence}일 주기
      </div>
    </div>
  )
}

/** 축 없는 미니 라인 — 회차 조회수 추이. 점 1개면 그리지 않는다(선이 성립하지 않음). */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const w = 100
  const h = 24
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const span = max - min || 1
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w
      const y = h - ((v - min) / span) * h
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-2.5 h-6 w-full" preserveAspectRatio="none" aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

/* ─────────────────────────────────────────────────────────────
   회차 상세 — 회차 테이블 + 신호 + 진단 + 편성 준수율
   ───────────────────────────────────────────────────────────── */
function ProgramDetail({
  creatorId,
  programId,
  baseline,
  onClose,
}: {
  creatorId: string
  programId: string
  baseline: HubChannelBaseline
  onClose: () => void
}) {
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')
  const [data, setData] = useState<HubProgramDetails | null>(null)

  useEffect(() => {
    let alive = true
    setState('loading')
    fetchHubProgramDetails(creatorId, programId)
      .then((d) => {
        if (!alive) return
        setData(d)
        setState('ok')
      })
      .catch(() => {
        if (!alive) return
        setState('error')
      })
    return () => {
      alive = false
    }
  }, [creatorId, programId])

  // 회차 테이블의 "채널 평균 대비"는 가장 짧은 기준선 창(3m)을 쓴다 — 최근 성과와 비교해야
  // 의미가 있고, 목록 응답에 이미 실려 오므로 추가 왕복이 없다.
  const base = useMemo(() => {
    const keys = Object.keys(baseline?.windows ?? {})
    const first = keys.map((k) => baseline.windows[k]).find((w) => w && w.n > 0)
    return first ?? null
  }, [baseline])

  const eps = useMemo(() => {
    const list = [...(data?.episodeAnalytics ?? [])]
    // 최신 회차가 위 — 지금 무엇을 바꿀지 보는 화면이라 최근이 먼저다.
    list.sort((a, b) => (b.episodeNumber ?? 0) - (a.episodeNumber ?? 0))
    return list
  }, [data])

  return (
    <div className="mt-4 border border-brand-blue/40 bg-surface-soft/40 p-[16px_18px]">
      <div className="flex items-center justify-between gap-3">
        <div className="font-text text-[13px] font-medium text-ink-800">
          {data?.program.name ?? '회차별 트래킹'}
          <span className="ml-2 font-text text-[11px] font-normal text-fg-3">회차별 트래킹</span>
        </div>
        <button onClick={onClose} className="font-text text-[12px] text-fg-3 hover:text-fg-1">
          닫기
        </button>
      </div>

      {state === 'loading' && <Skeleton />}
      {state === 'error' && (
        <Empty title="회차 정보를 불러오지 못했습니다" sub="잠시 후 다시 시도해주세요." />
      )}

      {state === 'ok' && data && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip
              level={data.diagnostics.trend.level}
              label="추세"
              value={data.diagnostics.trend.verdict}
              note={
                data.diagnostics.trend.deltaPct == null
                  ? '비교할 이전 회차 부족'
                  : `최근 ${data.diagnostics.trend.windowSize}화 평균 ${data.diagnostics.trend.deltaPct >= 0 ? '+' : ''}${data.diagnostics.trend.deltaPct.toFixed(1)}%`
              }
            />
            <Chip
              label="편성"
              value={
                data.cadenceAdherence.actualAvgIntervalDays == null
                  ? '—'
                  : `평균 ${data.cadenceAdherence.actualAvgIntervalDays.toFixed(1)}일`
              }
              note={
                data.cadenceAdherence.targetIntervalDays == null
                  ? '목표 주기 미설정'
                  : `목표 ${data.cadenceAdherence.targetIntervalDays}일 · 준수 ${data.cadenceAdherence.onScheduleRatio ?? 0}%`
              }
            />
            {data.diagnostics.weakness.key !== 'none' && (
              <Chip level="yellow" label="약점" value={data.diagnostics.weakness.label} note={data.diagnostics.weakness.detail} />
            )}
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse font-text text-[12.5px]">
              <thead>
                <tr className="whitespace-nowrap border-b border-line-soft text-left text-[11px] tracking-[0.08em] text-fg-3">
                  <th className="py-2 pr-3 font-normal">회차</th>
                  <th className="py-2 pr-3 text-right font-normal">조회수</th>
                  <th className="py-2 pr-3 text-right font-normal">시청시간비율</th>
                  <th className="py-2 pr-3 text-right font-normal">참여율</th>
                  <th className="py-2 pr-3 text-right font-normal">구독전환</th>
                  <th className="py-2 pr-3 font-normal">유형</th>
                  <th className="py-2 font-normal">진단</th>
                </tr>
              </thead>
              <tbody>
                {eps.map((e) => (
                  <EpisodeRow key={e.ytVideoId} ep={e} baseWatch={base?.watch ?? null} baseEng={base?.eng ?? null} />
                ))}
              </tbody>
            </table>
          </div>

          {base && (
            <p className="mt-2 font-text text-[11px] text-fg-3">
              ↕ 화살표는 <b>채널 평균 대비</b>입니다 (최근 {Math.round(base.windowDays / 30)}개월 ·
              시청시간비율 {pct(base.watch)} · 참여율 {pct(base.eng, 2)}).
            </p>
          )}

          {data.diagnostics.successPatterns.length > 0 && (
            <div className="mt-3 border-t border-line-hairline pt-3">
              <div className="font-text text-[11px] tracking-[0.08em] text-fg-3">성공 패턴</div>
              <ul className="mt-1.5 space-y-1">
                {data.diagnostics.successPatterns.map((s) => (
                  <li key={s.label} className="font-text text-[12px] text-fg-2">
                    <span className="text-brand-pink">▪</span> {s.label}
                    {s.detail ? <span className="text-fg-3"> — {s.detail}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function EpisodeRow({
  ep,
  baseWatch,
  baseEng,
}: {
  ep: HubEpisodeAnalytics
  baseWatch: number | null
  baseEng: number | null
}) {
  const netSubs =
    ep.subsGained == null && ep.subsLost == null ? null : (ep.subsGained ?? 0) - (ep.subsLost ?? 0)
  return (
    <tr className="border-b border-line-hairline align-top">
      <td className="py-2.5 pr-3">
        <span className="flex items-start gap-2">
          <span
            className={['mt-1.5 inline-block h-[6px] w-[6px] flex-none rounded-full', SIGNAL_CLASS[ep.signals.composite]].join(' ')}
            aria-hidden
          />
          <span className="min-w-0">
            <span className="block font-medium text-ink-800">
              {ep.episodeNumber == null ? '—' : `EP.${ep.episodeNumber}`}
            </span>
            <span className="mt-0.5 block max-w-[240px] truncate text-[11px] text-fg-3">
              {ep.title}
            </span>
          </span>
        </span>
      </td>
      <td className="py-2.5 pr-3 text-right">
        <span className="block tabular-nums font-medium text-ink-800">{count(ep.viewCount)}</span>
        {ep.signals.deltaVsAvg != null && (
          <span
            className={[
              'block text-[11px] tabular-nums',
              ep.signals.deltaVsAvg >= 0 ? 'text-status-success' : 'text-status-danger',
            ].join(' ')}
          >
            평균 {ep.signals.deltaVsAvg >= 0 ? '+' : ''}
            {ep.signals.deltaVsAvg.toFixed(0)}%
          </span>
        )}
      </td>
      <td className="py-2.5 pr-3 text-right">
        <span className="block tabular-nums text-fg-2">{pct(ep.avgViewPercentage)}</span>
        <VsBase value={ep.avgViewPercentage} base={baseWatch} />
      </td>
      <td className="py-2.5 pr-3 text-right">
        <span className="block tabular-nums text-fg-2">{pct(ep.signals.engagementRate, 2)}</span>
        <VsBase value={ep.signals.engagementRate} base={baseEng} />
      </td>
      <td className="py-2.5 pr-3 text-right tabular-nums text-fg-2">{signed(netSubs)}</td>
      <td className="whitespace-nowrap py-2.5 pr-3 text-[11px] text-fg-3">{QUADRANT_LABEL[ep.signals.quadrant]}</td>
      <td className="py-2.5 text-[11px] text-fg-2">{ep.signals.diagnosis}</td>
    </tr>
  )
}

/** 채널 평균 대비 화살표. 값·기준선 어느 쪽이든 없으면 아무것도 그리지 않는다. */
function VsBase({ value, base }: { value: number | null; base: number | null }) {
  if (value == null || base == null || base === 0) return null
  const delta = ((value - base) / base) * 100
  const up = delta >= 0
  return (
    <span
      className={[
        'block text-[11px] tabular-nums',
        up ? 'text-status-success' : 'text-status-danger',
      ].join(' ')}
    >
      {up ? '↑' : '↓'} {Math.abs(delta).toFixed(0)}%
    </span>
  )
}

function Chip({
  level,
  label,
  value,
  note,
}: {
  level?: HubSignalLevel
  label: string
  value: string
  note?: string
}) {
  return (
    <div className="border border-line-soft bg-paper px-3 py-2">
      <div className="flex items-center gap-1.5 font-text text-[10px] tracking-[0.08em] text-fg-3">
        {level && (
          <span className={['inline-block h-[6px] w-[6px] rounded-full', SIGNAL_CLASS[level]].join(' ')} aria-hidden />
        )}
        {label}
      </div>
      <div className="mt-0.5 font-text text-[13px] font-medium text-ink-800">{value}</div>
      {note && <div className="mt-0.5 font-text text-[11px] text-fg-3">{note}</div>}
    </div>
  )
}

function Empty({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mt-4 border border-dashed border-line-soft p-6 text-center">
      <p className="font-text text-[13px] font-medium text-fg-2">{title}</p>
      <p className="mt-1 font-text text-[12px] text-fg-3">{sub}</p>
    </div>
  )
}

function Skeleton() {
  return (
    <div className="mt-4 space-y-2" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[38px] w-full bg-surface-soft" />
      ))}
    </div>
  )
}
